// Template component imports
import ModernTemplate from './ModernTemplate'
import TraditionalTemplate from './TraditionalTemplate'
import MinimalTemplate from './MinimalTemplate'
import ExecutiveTemplate from './ExecutiveTemplate'
import TechnicalTemplate from './TechnicalTemplate'
import AspireTemplate from './AspireTemplate'
import FunctionalTemplate from './FunctionalTemplate'
import CombinedTemplate from './CombinedTemplate'
import HybridTemplate from './HybridTemplate'
import CreativeTemplate from './CreativeTemplate'
import CoralPinkTemplate from './CoralPinkTemplate'
import LimeGreenTemplate from './LimeGreenTemplate'
import CrimsonRedTemplate from './CrimsonRedTemplate'

// Template metadata interface
export interface TemplateMetadata {
  id: string
  name: string
  title: string
  description: string
  component: React.ComponentType<any>
  gradient: string
  primaryColor: string
  accentClass: string
  category: 'professional' | 'creative' | 'minimal' | 'colorful'
  popular?: boolean
}

// Template registry with metadata
export const templateRegistry: Record<string, TemplateMetadata> = {
  modern: {
    id: 'modern',
    name: 'modern',
    title: 'Modern Professional',
    description: 'Clean design for tech and business',
    component: ModernTemplate,
    gradient: 'from-blue-500 via-indigo-500 to-purple-600',
    primaryColor: '#3b82f6',
    accentClass: 'border-blue-500 bg-blue-50',
    category: 'professional',
    popular: true,
  },
  traditional: {
    id: 'traditional',
    name: 'traditional',
    title: 'Traditional Classic',
    description: 'Timeless design for formal roles',
    component: TraditionalTemplate,
    gradient: 'from-gray-700 via-gray-800 to-black',
    primaryColor: '#1f2937',
    accentClass: 'border-gray-700 bg-gray-50',
    category: 'professional',
  },
  minimal: {
    id: 'minimal',
    name: 'minimal',
    title: 'Minimal Clean',
    description: 'Simple and elegant layout',
    component: MinimalTemplate,
    gradient: 'from-slate-600 via-slate-700 to-slate-800',
    primaryColor: '#475569',
    accentClass: 'border-slate-600 bg-slate-50',
    category: 'minimal',
  },
  executive: {
    id: 'executive',
    name: 'executive',
    title: 'Executive Classic',
    description: 'Sophisticated for senior roles',
    component: ExecutiveTemplate,
    gradient: 'from-gray-800 via-gray-900 to-black',
    primaryColor: '#111827',
    accentClass: 'border-gray-800 bg-gray-50',
    category: 'professional',
  },
  technical: {
    id: 'technical',
    name: 'technical',
    title: 'Technical Focus',
    description: 'Skills-first for developers',
    component: TechnicalTemplate,
    gradient: 'from-green-500 via-teal-600 to-blue-700',
    primaryColor: '#10b981',
    accentClass: 'border-green-500 bg-green-50',
    category: 'professional',
    popular: true,
  },
  aspire: {
    id: 'aspire',
    name: 'aspire',
    title: 'Aspire Modern',
    description: 'Contemporary professional design',
    component: AspireTemplate,
    gradient: 'from-indigo-500 via-purple-500 to-pink-500',
    primaryColor: '#6366f1',
    accentClass: 'border-indigo-500 bg-indigo-50',
    category: 'professional',
  },
  functional: {
    id: 'functional',
    name: 'functional',
    title: 'Functional Skills',
    description: 'Emphasizes capabilities',
    component: FunctionalTemplate,
    gradient: 'from-teal-500 via-cyan-600 to-blue-600',
    primaryColor: '#14b8a6',
    accentClass: 'border-teal-500 bg-teal-50',
    category: 'professional',
  },
  combined: {
    id: 'combined',
    name: 'combined',
    title: 'Combined Format',
    description: 'Balanced approach',
    component: CombinedTemplate,
    gradient: 'from-violet-500 via-purple-600 to-fuchsia-600',
    primaryColor: '#8b5cf6',
    accentClass: 'border-violet-500 bg-violet-50',
    category: 'professional',
  },
  hybrid: {
    id: 'hybrid',
    name: 'hybrid',
    title: 'Hybrid Design',
    description: 'Mix of styles',
    component: HybridTemplate,
    gradient: 'from-cyan-500 via-blue-500 to-indigo-600',
    primaryColor: '#06b6d4',
    accentClass: 'border-cyan-500 bg-cyan-50',
    category: 'professional',
  },
  creative: {
    id: 'creative',
    name: 'creative',
    title: 'Creative Edge',
    description: 'Bold layout for creatives',
    component: CreativeTemplate,
    gradient: 'from-pink-500 via-purple-500 to-indigo-600',
    primaryColor: '#ec4899',
    accentClass: 'border-purple-500 bg-purple-50',
    category: 'creative',
    popular: true,
  },
  'coral-pink': {
    id: 'coral-pink',
    name: 'coral-pink',
    title: 'Coral Pink',
    description: 'Warm coral and pink tones',
    component: CoralPinkTemplate,
    gradient: 'from-orange-400 via-pink-400 to-rose-500',
    primaryColor: '#f472b6',
    accentClass: 'border-pink-400 bg-pink-50',
    category: 'colorful',
  },
  'lime-green': {
    id: 'lime-green',
    name: 'lime-green',
    title: 'Lime Green',
    description: 'Fresh lime green design',
    component: LimeGreenTemplate,
    gradient: 'from-lime-400 via-green-500 to-emerald-600',
    primaryColor: '#84cc16',
    accentClass: 'border-lime-500 bg-lime-50',
    category: 'colorful',
  },
  'crimson-red': {
    id: 'crimson-red',
    name: 'crimson-red',
    title: 'Crimson Red',
    description: 'Bold crimson red theme',
    component: CrimsonRedTemplate,
    gradient: 'from-red-500 via-rose-600 to-pink-600',
    primaryColor: '#ef4444',
    accentClass: 'border-red-500 bg-red-50',
    category: 'colorful',
  },
}

// Helper function to get template by ID
export const getTemplate = (templateId: string): TemplateMetadata | undefined => {
  return templateRegistry[templateId]
}

// Get all templates as array
export const getAllTemplates = (): TemplateMetadata[] => {
  return Object.values(templateRegistry)
}

// Get templates by category
export const getTemplatesByCategory = (category: string): TemplateMetadata[] => {
  return getAllTemplates().filter(template => template.category === category)
}

// Get popular templates
export const getPopularTemplates = (): TemplateMetadata[] => {
  return getAllTemplates().filter(template => template.popular)
}

// Export all template components
export {
  ModernTemplate,
  TraditionalTemplate,
  MinimalTemplate,
  ExecutiveTemplate,
  TechnicalTemplate,
  AspireTemplate,
  FunctionalTemplate,
  CombinedTemplate,
  HybridTemplate,
  CreativeTemplate,
  CoralPinkTemplate,
  LimeGreenTemplate,
  CrimsonRedTemplate,
}
